/**
 * Contact Code Service
 *
 * Allows users with private profiles to share contact codes
 * that can be used to add them as a contact.
 *
 * Code format: 6 random alphanumeric characters (e.g., AB3XY9)
 * Expiration: 15 minutes
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

const CODE_LENGTH = 6;
const EXPIRATION_MINUTES = 15;

/**
 * Generate a random contact code
 * Format: 6 uppercase alphanumeric characters
 */
function generateRandomCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Create a new contact code for a user
 * @param {string} userId - User ID
 * @param {string} displayName - User's display name (for reference)
 * @returns {Promise<{code: string, expiresAt: Date}>}
 */
export async function createContactCode(userId, displayName) {
  try {
    // Generate unique code
    let code = generateRandomCode();
    let exists = true;
    let attempts = 0;

    // Ensure code is unique (unlikely but possible)
    while (exists && attempts < 5) {
      const codeRef = doc(db, 'contactCodes', code);
      const codeSnap = await getDoc(codeRef);
      if (!codeSnap.exists()) {
        exists = false;
      } else {
        code = generateRandomCode();
        attempts++;
      }
    }

    if (exists) {
      throw new Error('Failed to generate unique contact code');
    }

    // Calculate expiration time (15 minutes from now)
    const expiresAt = new Date();
    expiresAt.setMinutes(expiresAt.getMinutes() + EXPIRATION_MINUTES);

    // Store contact code
    const codeRef = doc(db, 'contactCodes', code);
    await setDoc(codeRef, {
      code,
      userId,
      displayName,
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(expiresAt),
      used: false,
      usedBy: null,
      usedAt: null
    });

    return {
      code,
      expiresAt
    };
  } catch (error) {
    console.error('Error creating contact code:', error);
    throw error;
  }
}

/**
 * Get the current/active contact code for a user
 * Returns the most recent non-expired code
 * @param {string} userId - User ID
 * @returns {Promise<{code: string, expiresAt: Date} | null>}
 */
export async function getUserContactCode(userId) {
  try {
    const q = query(
      collection(db, 'contactCodes'),
      where('userId', '==', userId),
      where('used', '==', false)
    );

    const snapshot = await getDocs(q);

    // Find non-expired code
    const now = new Date();
    for (const doc of snapshot.docs) {
      const data = doc.data();
      const expiresAt = data.expiresAt?.toDate?.() || new Date(0);

      if (expiresAt > now) {
        return {
          code: data.code,
          expiresAt,
          createdAt: data.createdAt?.toDate?.()
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting user contact code:', error);
    return null;
  }
}

/**
 * Validate a contact code and get the user ID
 * @param {string} code - Contact code to validate
 * @returns {Promise<{userId: string, displayName: string} | null>}
 */
export async function validateContactCode(code) {
  try {
    const codeRef = doc(db, 'contactCodes', code.toUpperCase());
    const codeSnap = await getDoc(codeRef);

    if (!codeSnap.exists()) {
      console.error('Contact code not found');
      return null;
    }

    const data = codeSnap.data();

    // Check if already used
    if (data.used) {
      console.error('Contact code already used');
      return null;
    }

    // Check expiration
    const expiresAt = data.expiresAt?.toDate?.() || new Date(0);
    if (new Date() > expiresAt) {
      console.error('Contact code expired');
      return null;
    }

    return {
      userId: data.userId,
      displayName: data.displayName
    };
  } catch (error) {
    console.error('Error validating contact code:', error);
    return null;
  }
}

/**
 * Mark a contact code as used
 * @param {string} code - Contact code
 * @param {string} usedBy - User ID who used the code
 * @returns {Promise<void>}
 */
export async function markContactCodeUsed(code, usedBy) {
  try {
    const codeRef = doc(db, 'contactCodes', code.toUpperCase());
    await setDoc(codeRef, {
      used: true,
      usedBy,
      usedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error marking contact code as used:', error);
    throw error;
  }
}

/**
 * Delete/revoke a contact code
 * @param {string} userId - User ID
 * @param {string} code - Contact code to revoke
 * @returns {Promise<void>}
 */
export async function revokeContactCode(userId, code) {
  try {
    const codeRef = doc(db, 'contactCodes', code.toUpperCase());
    const codeSnap = await getDoc(codeRef);

    // Verify ownership
    if (!codeSnap.exists() || codeSnap.data().userId !== userId) {
      throw new Error('Unauthorized: cannot revoke code you do not own');
    }

    // Delete the code
    await deleteDoc(codeRef);
  } catch (error) {
    console.error('Error revoking contact code:', error);
    throw error;
  }
}

/**
 * Get time remaining for a contact code in seconds
 * @param {Date} expiresAt - Expiration timestamp
 * @returns {number} Seconds remaining (0 if expired)
 */
export function getTimeRemaining(expiresAt) {
  if (!expiresAt) return 0;
  const remaining = expiresAt.getTime() - new Date().getTime();
  return Math.max(0, Math.floor(remaining / 1000));
}

/**
 * Format time remaining as human-readable string
 * @param {number} seconds - Seconds remaining
 * @returns {string} e.g. "14m 32s"
 */
export function formatTimeRemaining(seconds) {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (minutes === 0) {
    return `${secs}s`;
  }

  return `${minutes}m ${secs}s`;
}

export default {
  createContactCode,
  getUserContactCode,
  validateContactCode,
  markContactCodeUsed,
  revokeContactCode,
  getTimeRemaining,
  formatTimeRemaining
};
